<template>
  <v-row justify="center">
    <v-dialog v-model="dialog" persistent max-width="512" style="height: 90vh">
      <v-card class="fill-height">
        <v-card-title class="headline" style="height: 7vh">{{ $i18n.t('settings.addUserLayers') }}</v-card-title>
        <v-card-text style="height: 75vh; overflow: auto" class="pt-2">
          <v-form ref="form" v-model="isFormValid">
            <v-row no-gutters>
              <v-col xs="12" class="py-0">
                <v-text-field
                  outlined
                  v-model="layerNamePl"
                  :label="$i18n.t('settings.polishLayerName')"
                  :rules="[rules.required]"
                />
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col xs="12" class="py-0">
                <v-text-field
                  outlined
                  v-model="layerNameEn"
                  :label="$i18n.t('settings.englishLayerName')"
                  :rules="[rules.required]"
                />
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col xs="12" class="py-0">
                <v-select
                  :attach="true"
                  :items="geometryTypes"
                  v-model="geometryType"
                  :label="$i18n.t('settings.geometryType')"
                  outlined
                  item-text="text"
                  item-value="value"
                  :rules="[rules.required]"
                />
              </v-col>
            </v-row>
          </v-form>
          <template v-if="geometryType">
            <v-row no-gutters>
              <v-subheader class="pl-4">{{ $i18n.t('settings.layerStyle') }}</v-subheader>
            </v-row>
            <v-row no-gutters>
              <layer-style
                :geometry-type="geometryType"
                :layer-style.sync="newLayerStyle"
                :is-style-valid.sync="isStyleValid"
                is-new-user-layer
              />
            </v-row>
          </template>
          <v-row no-gutters>
            <v-subheader class="pl-4">{{ $i18n.t('settings.attributes') }}</v-subheader>
          </v-row>

          <v-form ref="attributesForm" v-model="isAttributesFormValid">
            <v-row no-gutters>
              <v-col cols="5" class="pr-2">
                <v-text-field
                  outlined
                  :label="$i18n.t('settings.polishAttributeName')"
                  :rules="[rules.required, rules.notExistsPl]"
                  v-model="newAttributePl"
                />
              </v-col>
              <v-col cols="5" class="pl-2">
                <v-text-field
                  outlined
                  :label="$i18n.t('settings.englishAttributeName')"
                  :rules="[rules.required, rules.notExistsEn]"
                  v-model="newAttributeEn"
                />
              </v-col>
              <v-col cols="2" style="text-align:center;">
                <v-icon
                  @click="addAttribute"
                  style="padding-top: 16px"
                  :disabled="!isAttributesFormValid"
                  :title="$i18n.t('settings.addAttribute')"
                >mdi-plus-circle-outline</v-icon>
              </v-col>
            </v-row>
          </v-form>
          <span v-if="newAttributesList.length > 0">
            <v-row no-gutters>
              <v-subheader class="pl-4">
                {{
                $i18n.t('settings.attributesList')
                }}
              </v-subheader>
            </v-row>
            <v-row no-gutters>
              <draggable
                v-model="newAttributesList"
                @start="drag = true"
                @end="drag = false"
                style="width: 100%"
              >
                <v-list-item
                  class="layerSettings__list"
                  v-for="(attr, idx) in newAttributesList"
                  :key="idx"
                >
                  <v-list-item-content>
                    <v-col cols="5">
                      <v-list-item-title>{{ attr.pl }}</v-list-item-title>
                    </v-col>
                    <v-col cols="5">
                      <v-list-item-title>{{ attr.en }}</v-list-item-title>
                    </v-col>
                    <v-col cols="2">
                      <v-icon
                        @click="removeAttribute(idx)"
                        :title="$i18n.t('settings.deleteAttribute')"
                      >mdi-delete</v-icon>
                    </v-col>
                  </v-list-item-content>
                </v-list-item>
              </draggable>
            </v-row>
          </span>
        </v-card-text>
        <v-card-actions style="height: 8vh">
          <div class="flex-grow-1" />
          <v-btn color="primary" text @click="closeDialog">{{ $i18n.t('default.cancel') }}</v-btn>
          <v-btn
            color="primary"
            text
            @click="confirmSuccess"
            :disabled="!isValid"
          >{{ $i18n.t('default.add') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import draggable from 'vuedraggable';

export default {
  components: {
    draggable,
    LayerStyle: () => import('@/components/settings/LayerStyle')
  },
  props: {
    dialog: {
      required: true,
      type: Boolean
    }
  },
  computed: {
    isValid() {
      return this.isFormValid && this.isStyleValid;
    },
    userLayers() {
      return this.$store.getters.getUserLayersList;
    },
    rules() {
      return {
        required: value => !!value || this.$i18n.t('default.requiredField'),
        notExistsPl: value =>
          !value ||
          !this.newAttributesList.find(newAtt => newAtt.pl === value) ||
          this.$i18n.t('settings.attributeAlreadyExists'),
        notExistsEn: value =>
          !value ||
          !this.newAttributesList.find(newAtt => newAtt.en === value) ||
          this.$i18n.t('settings.attributeAlreadyExists')
      };
    }
  },
  data: vm => ({
    drag: false,
    geometryType: '',
    geometryTypes: [
      { text: vm.$i18n.t('default.point'), value: 'point' },
      { text: vm.$i18n.t('default.line'), value: 'line' },
      { text: vm.$i18n.t('default.polygon'), value: 'polygon' }
    ],
    isAttributesFormValid: false,
    isFormValid: false,
    isStyleValid: false,
    layerNamePl: '',
    layerNameEn: '',
    newAttributeEn: '',
    newAttributePl: '',
    newAttributesList: [],
    newLayerStyle: {
      type: 'single',
      fillColor: '#00A99D',
      strokeColor: '#00A99D',
      strokeWidth: 1,
      opacity: 0
    }
  }),
  methods: {
    addAttribute() {
      this.newAttributesList.push({
        pl: this.newAttributePl,
        en: this.newAttributeEn
      });
      this.newAttributeEn = '';
      this.newAttributePl = '';
      this.$refs.attributesForm.reset();
    },
    closeDialog() {
      this.$emit('cancel');
    },
    confirmSuccess() {
      const payload = {
        layer_type: 'user',
        name_pl: this.layerNamePl,
        name_en: this.layerNameEn,
        fields: this.newAttributesList,
        style: this.newLayerStyle,
        geometry_type: this.geometryType
      };
      this.$emit('success', payload);
      // this.$store.dispatch('addLayer', payload).then(r => {
      //   if (r.status === 201) {
      //     this.$root.$emit('showSnackbar', this.$i18n.t(`settings.addSuccess`));
      //     this.$emit('success');
      //     delete payload.layer_type;
      //     payload.id = r.data.layers;
      //     this.$store.commit('setUserList', this.userLayers.concat(payload));
      //     this.layerNamePl = '';
      //     this.layerNameEn = '';
      //     this.fillColor = '#00A99D';
      //     this.strokeColor = '#00A99D';
      //     this.strokeWidth = 1;
      //     this.opacity = 0;
      //     this.geometryType = 'point';
      //     this.isFillPickerVisible = false;
      //     this.isStrokePickerVisible = false;
      //     this.newAttributeEn = undefined;
      //     this.newAttributePl = undefined;
      //     this.newAttributesList = [];
      //   }
      // });
    },
    onDialogClose() {
      this.$refs.attributesForm.reset();
      this.$refs.form.reset();
      this.geometryType = '';
      this.newAttributesList = [];
      this.newLayerStyle = {
        type: 'single',
        fillColor: '#00A99D',
        strokeColor: '#00A99D',
        strokeWidth: 1,
        opacity: 0
      };
    },
    removeAttribute(idx) {
      this.newAttributesList.splice(idx, 1);
    }
  },
  watch: {
    dialog(nV) {
      if (nV) {
        return;
      }
      this.onDialogClose();
    }
  }
};
</script>

<style lang="scss">
#opacity-subheader--edit {
  position: absolute;
  top: 465px !important;
}
#opacity-slider-column {
  .v-input__slider {
    position: absolute;
    top: 485px;
    width: 230px;
  }
  .v-slider {
    margin-left: 0;
  }
}
.v-input__slider {
  margin-top: 15px;
}
.upload {
  text-align: center;
  label {
    cursor: pointer;
  }
}
.pa4 {
  padding: 16px;
}
.layerSettings__list {
  padding: 0px;
  transition: 0.2s all;
  &:not(:last-child) {
    border-bottom: 1px solid lightgrey;
  }
  &:hover {
    background-color: rgb(243, 243, 243);
  }
  .v-list-item__content {
    padding: 0px;
    text-align: center;
  }
}
</style>
